import { Button } from '@creditornot/cb-button';
import { typographyCss } from '@creditornot/cb-ingredients';
import { Input, InputAlert } from '@creditornot/cb-input';
import { DateTime } from 'luxon';
import R from 'ramda';
import React, { useMemo } from 'react';
import { Field, Form } from 'react-final-form';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';
import * as yup from 'yup';

import * as api from 'apis/restaurant-api/merchant-admin/purchases/api';
import validateYupSchema from 'common/utils/validateYupSchema';
import { telemetryMerchantAdminPurchasesInteractionGetReceiptsConfirmed } from 'modules/purchases/telemetry';

import IsoDateRangePickerInput, { Props as IsoDateRangePickerInputProps } from './IsoDateRangePickerInput';
import { reportGetReceipts } from './behavioral';

type FormValues = {
  dateRange: IsoDateRangePickerInputProps['value'];
  email: string;
};

type Props = {
  className?: string;
  initialDateRange: IsoDateRangePickerInputProps['value'];
  onClose: () => void;
  venueId: string;
};

const Root = styled.div``;

const Explanation = styled.div`
  ${typographyCss.Body3}
  margin-block-end: 16px;
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-block-end: 16px;
`;

const FieldValidationError = styled(InputAlert).attrs({ variant: 'error' })`
  margin-block-start: 8px;
`;

const SubmitButton = styled(Button).attrs({
  size: 'small',
  type: 'submit',
  variant: 'lightBlue',
})`
  display: block;
  margin-inline-start: auto;
`;

const GetReceipts = ({ className, initialDateRange, onClose, venueId }: Props) => {
  const intl = useIntl();
  const { cbDirection } = useTheme();

  const handleSubmit = async (values: FormValues) => {
    if (!values.dateRange) {
      return;
    }

    reportGetReceipts();

    try {
      await api.requestPurchaseReceiptArchive(
        values.email,
        venueId,
        values.dateRange.from,
        DateTime.fromISO(values.dateRange.to).toISODate(), // the end date is inclusive in the backend API for this endpoint
      );
      toast.success(<FormattedMessage id="purchases.getReceiptsSuccess" />);
      telemetryMerchantAdminPurchasesInteractionGetReceiptsConfirmed();
    } catch (_) {
      toast.error(<FormattedMessage id="errors.unknown-error-retry-contact" />);
    }

    onClose();
  };

  const validationSchema = useMemo(() => {
    return yup
      .object({
        dateRange: yup
          .object({
            from: yup
              .string()
              .nullable()
              .required(intl.formatMessage({ id: 'purchases.start-date-missing' })),
            to: yup
              .string()
              .nullable()
              .required(intl.formatMessage({ id: 'purchases.end-date-missing' })),
          })
          .required(intl.formatMessage({ id: 'validation.required' })),
        email: yup
          .string()
          .trim()
          .email(intl.formatMessage({ id: 'validation.emailError' }))
          .required(intl.formatMessage({ id: 'validation.required' })),
      })
      .required();
  }, [intl]);

  return (
    <Root className={className} dir={cbDirection}>
      <Explanation>
        <FormattedMessage id="purchases.receiptArchiveExplanation" />
      </Explanation>

      <Form<FormValues>
        initialValues={{ dateRange: initialDateRange, email: '' }}
        initialValuesEqual={R.equals}
        onSubmit={handleSubmit}
        validate={validateYupSchema(validationSchema)}
      >
        {({ handleSubmit, invalid, submitting }) => (
          <form onSubmit={handleSubmit}>
            <Field<IsoDateRangePickerInputProps['value']> name="dateRange" parse={v => v} format={v => v}>
              {field => (
                <FieldWrapper>
                  <IsoDateRangePickerInput
                    {...field.input}
                    disabledDays={{
                      before: DateTime.fromISO('2022-10-19').toJSDate(),
                      after: new Date(),
                    }}
                    placeholder={intl.formatMessage({ id: 'purchases.choose-dates' })}
                    popoverRendering="inline"
                    popoverZIndex={100}
                  />
                  {field.meta.touched && field.meta.error && (
                    <>
                      {field.meta.error.from && <FieldValidationError>{field.meta.error.from}</FieldValidationError>}
                      {field.meta.error.to && <FieldValidationError>{field.meta.error.to}</FieldValidationError>}
                    </>
                  )}
                </FieldWrapper>
              )}
            </Field>
            <Field name="email" parse={v => v}>
              {field => (
                <FieldWrapper>
                  <Input
                    {...field.input}
                    placeholder={intl.formatMessage({ id: 'purchases.email-placeholder' })}
                    readOnly={submitting}
                  />
                  {field.meta.touched && field.meta.error && (
                    <FieldValidationError>{field.meta.error}</FieldValidationError>
                  )}
                </FieldWrapper>
              )}
            </Field>
            <SubmitButton disabled={invalid || submitting}>
              <FormattedMessage id="purchases.send" />
            </SubmitButton>
          </form>
        )}
      </Form>
    </Root>
  );
};

export default GetReceipts;
